// Admin: manual organizer verification queue.
// Shows every organizer who submitted phone + photos for review, with
// approve / reject actions. Replaces the Twilio + Stripe Identity flow.
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  CheckCircle2, XCircle, Clock, ShieldCheck, Mail, Phone, User, MapPin,
} from "lucide-react";

const STATUS_STYLE = {
  pending:  { bg: "rgba(249,115,22,0.10)", ink: "var(--avt-orange)", label: "Pending" },
  approved: { bg: "rgba(132,204,22,0.14)", ink: "#4B7A0A", label: "Approved" },
  rejected: { bg: "rgba(196,79,54,0.10)", ink: "var(--terracotta)", label: "Rejected" },
};

export default function AdminVerifications() {
  const { user, loading: authLoading } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejectDraft, setRejectDraft] = useState({}); // {user_id: reason}

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/admin/organizers/manual-queue");
      setItems(data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.is_admin) load();
  }, [user, load]);

  if (authLoading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const approve = async (uid) => {
    if (!window.confirm("Approve this organizer? They'll get an email + verified badge immediately.")) return;
    setBusyId(uid);
    try {
      await api.post(`/admin/organizers/${uid}/manual-approve`);
      toast.success("Approved — email sent.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusyId(""); }
  };

  const reject = async (uid) => {
    const reason = (rejectDraft[uid] || "").trim();
    if (reason.length < 3) {
      toast.error("Add a short reason so they know what to fix.");
      return;
    }
    setBusyId(uid);
    try {
      await api.post(`/admin/organizers/${uid}/manual-reject`, { reason });
      toast.success("Rejected — email sent.");
      setRejectDraft((d) => ({ ...d, [uid]: "" }));
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusyId(""); }
  };

  const pending = items.filter((i) => i.manual_status === "pending");
  const reviewed = items.filter((i) => i.manual_status !== "pending");

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="admin-verifications-page">
      <div className="overline" style={{ color: "var(--terracotta)" }}>Admin · Verifications</div>
      <h1 className="font-serif text-5xl mt-3">Organizer verification queue.</h1>
      <p className="mt-3 text-sm max-w-2xl" style={{ color: "var(--ink-soft)" }}>
        Every organizer submission with their phone, ID photo, and venue info.
        Approve to unlock the verified badge + Stripe Connect payouts.
      </p>

      {error && (
        <div className="mt-6 rounded-md p-3 text-sm"
             style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}>
          {error}
        </div>
      )}

      <section className="mt-10">
        <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
          <Clock size={16} style={{ color: "var(--avt-orange)" }} />
          Pending review ({pending.length})
        </h2>
        {loading ? (
          <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading…</div>
        ) : pending.length === 0 ? (
          <div
            data-testid="admin-verifications-empty-pending"
            className="rounded-lg border p-8 text-center"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
              No pending submissions right now. Nice work! 🎉
            </p>
          </div>
        ) : (
          <div className="space-y-5" data-testid="admin-verifications-pending-list">
            {pending.map((it) => (
              <SubmissionCard
                key={it.user_id}
                item={it}
                busy={busyId === it.user_id}
                rejectReason={rejectDraft[it.user_id] || ""}
                setRejectReason={(v) => setRejectDraft((d) => ({ ...d, [it.user_id]: v }))}
                onApprove={() => approve(it.user_id)}
                onReject={() => reject(it.user_id)}
              />
            ))}
          </div>
        )}
      </section>

      {reviewed.length > 0 && (
        <section className="mt-14">
          <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
            <CheckCircle2 size={16} style={{ color: "#4B7A0A" }} />
            Recently reviewed ({reviewed.length})
          </h2>
          <div className="space-y-3" data-testid="admin-verifications-reviewed-list">
            {reviewed.slice(0, 20).map((it) => (
              <ReviewedRow key={it.user_id} item={it} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SubmissionCard({ item, busy, rejectReason, setRejectReason, onApprove, onReject }) {
  const style = STATUS_STYLE[item.manual_status] || STATUS_STYLE.pending;
  const legalName = [item.legal_first_name, item.legal_last_name].filter(Boolean).join(" ");
  return (
    <div
      data-testid={`admin-verification-card-${item.user_id}`}
      className="rounded-lg border p-6"
      style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="font-serif text-2xl leading-tight">
            {item.business_name || item.name || item.email}
          </div>
          <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
            <User size={11} className="inline mr-1" />
            {item.name || "—"}
            {legalName && ` (legal: ${legalName})`}
            <span className="mx-2">·</span>
            <Mail size={11} className="inline mr-1" />
            <Link
              to={`/admin/users?email=${encodeURIComponent(item.email)}`}
              className="underline"
            >
              {item.email}
            </Link>
            {item.email_verified && (
              <span className="ml-2 text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                    style={{ background: "rgba(132,204,22,0.15)", color: "#4B7A0A" }}>
                email ✓
              </span>
            )}
          </div>
        </div>
        <span
          className="text-xs uppercase tracking-widest px-2.5 py-1 rounded-full"
          style={{ background: style.bg, color: style.ink }}
        >
          {style.label}
        </span>
      </div>

      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
            Phone (for records)
          </div>
          <div className="font-mono text-sm" data-testid={`admin-verification-phone-${item.user_id}`}>
            <Phone size={12} className="inline mr-1" />
            {item.manual_phone || "—"}
          </div>
        </div>
        {item.manual_submitted_at && (
          <div>
            <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
              Submitted
            </div>
            <div className="text-sm">
              {new Date(item.manual_submitted_at).toLocaleString()}
            </div>
          </div>
        )}
      </div>

      {(item.manual_id_photo_url || item.manual_venue_photo_url) && (
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {item.manual_id_photo_url && (
            <PhotoBlock label="ID selfie" url={item.manual_id_photo_url}
                        testId={`admin-verification-id-${item.user_id}`} />
          )}
          {item.manual_venue_photo_url && (
            <PhotoBlock label="Venue info" url={item.manual_venue_photo_url}
                        testId={`admin-verification-venue-${item.user_id}`} />
          )}
        </div>
      )}
      {(!item.manual_id_photo_url && !item.manual_venue_photo_url) && (
        <div className="mt-4 text-xs italic" style={{ color: "var(--ink-soft)" }}>
          No in-app photos uploaded — check your admin inbox for their email attachments.
        </div>
      )}

      {item.manual_notes && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
            Their notes
          </div>
          <div className="text-sm whitespace-pre-line rounded-md p-3"
               style={{ background: "rgba(0,0,0,0.03)" }}>
            {item.manual_notes}
          </div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t space-y-3"
           style={{ borderColor: "rgba(0,0,0,0.08)" }}>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={onApprove}
            disabled={busy}
            data-testid={`admin-verification-approve-${item.user_id}`}
            className="btn-primary rounded-full px-6 py-2.5 inline-flex items-center gap-2"
          >
            <ShieldCheck size={14} /> Approve &amp; verify
          </Button>
        </div>
        <div className="space-y-2">
          <Textarea
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Reason to send them if you reject (short, kind)"
            rows={2}
            maxLength={400}
            data-testid={`admin-verification-reject-reason-${item.user_id}`}
          />
          <Button
            variant="outline"
            onClick={onReject}
            disabled={busy || rejectReason.trim().length < 3}
            data-testid={`admin-verification-reject-${item.user_id}`}
            className="rounded-full inline-flex items-center gap-2"
            style={{ color: "var(--terracotta)", borderColor: "var(--terracotta)" }}
          >
            <XCircle size={14} /> Reject with reason
          </Button>
        </div>
      </div>
    </div>
  );
}

function ReviewedRow({ item }) {
  const style = STATUS_STYLE[item.manual_status] || STATUS_STYLE.pending;
  return (
    <div
      data-testid={`admin-verification-reviewed-${item.user_id}`}
      className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3"
      style={{ borderColor: "rgba(0,0,0,0.08)", background: "#fff" }}
    >
      <div className="min-w-0">
        <div className="font-medium truncate">
          {item.business_name || item.name || item.email}
        </div>
        <div className="text-xs mt-0.5" style={{ color: "var(--ink-soft)" }}>
          {item.email}
          {item.manual_reviewed_at && (
            <> · reviewed {new Date(item.manual_reviewed_at).toLocaleDateString()}</>
          )}
          {item.manual_reviewed_by && (
            <> by {item.manual_reviewed_by}</>
          )}
        </div>
        {item.manual_reject_reason && (
          <div className="text-xs mt-1" style={{ color: "var(--terracotta)" }}>
            &ldquo;{item.manual_reject_reason}&rdquo;
          </div>
        )}
      </div>
      <span
        className="text-xs uppercase tracking-widest px-2 py-0.5 rounded-full"
        style={{ background: style.bg, color: style.ink }}
      >
        {style.label}
      </span>
    </div>
  );
}

function PhotoBlock({ label, url, testId }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest mb-1" style={{ color: "var(--ink-soft)" }}>
        {label}
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className="block rounded-md overflow-hidden border hover:shadow-md transition"
        style={{ borderColor: "rgba(0,0,0,0.1)" }}
      >
        <img
          src={url}
          alt={label}
          className="w-full aspect-[4/3] object-cover"
        />
      </a>
    </div>
  );
}
